import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, Plus, Save, Trash2 } from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const statuses = ["new", "contacted", "confirmed", "completed", "cancelled"];
const statusColors = { new: "#C88A2B", contacted: "#397D94", confirmed: "#244B36", completed: "#A96818", cancelled: "#7a2b2b" };
const difficulties = ["easy", "moderate", "active", "hard", "expedition"];
const excursionStatuses = ["draft", "pending", "published", "hidden", "archived"];

const fieldCls = "w-full bg-argali-obsidian border border-argali-brown/40 text-argali-ivory text-sm px-3 py-2 outline-none focus:border-argali-bronze";
const labelCls = "text-[0.6rem] uppercase tracking-[0.16em] text-argali-sandstone/60 mb-1 block";
const secondaryBtn = "border border-argali-brown/50 text-argali-sandstone hover:text-argali-gold hover:border-argali-bronze px-4 py-2 text-xs uppercase tracking-[0.12em] transition-colors";

const isManagerUser = (user) => user?.role === "owner" || user?.role === "admin";

/* ------------------------------- BOOKINGS ------------------------------- */
const BookingsTab = ({ user }) => {
  const manager = isManagerUser(user);
  const [items, setItems] = useState([]);
  const [guides, setGuides] = useState([]);
  const [filter, setFilter] = useState("all");

  const load = async () => {
    try {
      const req = await api.get("/admin/bookings");
      setItems(req.data);
      if (manager) {
        const g = await api.get("/admin/guides");
        setGuides(g.data);
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const update = async (id, patch) => {
    try {
      const { data } = await api.patch(`/admin/bookings/${id}`, patch);
      setItems((prev) => prev.map((it) => it.id === id ? { ...it, ...patch, ...data } : it));
      toast.success("Обновлено");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const guideName = (id) => guides.find((g) => g.id === id)?.name || "";
  const filtered = filter === "all" ? items : items.filter((i) => i.status === filter);

  return (
    <div>
      <p className="text-argali-sandstone/60 text-sm mb-6">{items.length} всего · {items.filter((i) => i.status === "new").length} новых</p>
      <div className="flex flex-wrap gap-2 mb-8">
        {["all", ...statuses].map((s) => (
          <button key={s} onClick={() => setFilter(s)} data-testid={`admin-filter-${s}`}
            className="text-[0.68rem] uppercase tracking-[0.14em] px-3.5 py-2 border transition-colors"
            style={{ borderColor: filter === s ? "#A96818" : "rgba(215,184,117,0.25)", background: filter === s ? "#A96818" : "transparent", color: filter === s ? "#FAFAF7" : "#D7B875" }}>
            {s}
          </button>
        ))}
      </div>

      <div className="space-y-3">
        {filtered.length === 0 && <p className="text-argali-sandstone/50">Заявок нет.</p>}
        {filtered.map((it) => (
          <div key={it.id} className="border border-argali-brown/30 p-5" data-testid={`admin-booking-${it.id}`}>
            <div className="grid grid-cols-1 lg:grid-cols-[1fr_270px] gap-6">
              <div>
                <div className="flex flex-wrap items-center gap-3">
                  <span className="w-2 h-2 rounded-full" style={{ background: statusColors[it.status] }} />
                  <span className="text-[0.6rem] uppercase tracking-[0.16em] text-argali-sandstone/60">{it.type === "custom_tour" ? "Индивидуальный тур" : "Бронирование"}</span>
                  <span className="text-[0.6rem] text-argali-sandstone/40">{new Date(it.created_at).toLocaleString("ru-RU")}</span>
                </div>
                <h3 className="font-serif text-2xl text-argali-ivory mt-2">{it.customer_name} · {it.phone}</h3>
                {it.excursion_title && <p className="text-argali-gold text-sm mt-1">{it.excursion_title}</p>}
                <div className="text-sm text-argali-ivory/70 mt-2 space-y-0.5">
                  {it.email && <p>Email: {it.email}</p>}
                  {it.telegram && <p>Telegram/WA: {it.telegram}</p>}
                  {it.requested_date && <p>Даты: {it.requested_date}</p>}
                  {it.dates && <p>Даты: {it.dates}</p>}
                  {it.adults != null && <p>Взрослые: {it.adults}{it.children ? `, дети: ${it.children}` : ""}</p>}
                  {it.people && <p>Человек: {it.people}{it.days ? `, дней: ${it.days}` : ""}</p>}
                  {it.interests && it.interests.length > 0 && <p>Интересы: {it.interests.join(", ")}</p>}
                  {it.comment && <p className="italic">«{it.comment}»</p>}
                  {it.route_notes && <p className="italic">«{it.route_notes}»</p>}
                  {it.assigned_guide_user_id && <p className="text-argali-gold mt-2">Гид: {guideName(it.assigned_guide_user_id) || (manager ? "Назначен" : user.name)}</p>}
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <label className={labelCls}>Статус</label>
                  <select value={it.status} onChange={(e) => update(it.id, { status: e.target.value })} data-testid={`admin-status-${it.id}`} className={fieldCls}>
                    {statuses.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                {manager && (
                  <div>
                    <label className={labelCls}>Назначить гида</label>
                    <select value={it.assigned_guide_user_id || ""} onChange={(e) => update(it.id, { assigned_guide_user_id: e.target.value || null })} className={fieldCls}>
                      <option value="">Не назначен</option>
                      {guides.filter((g) => g.status === "active").map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
                    </select>
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

/* -------------------------------- GUIDES -------------------------------- */
const emptyGuide = {
  id: null, name: "", email: "", phone: "", password: "", status: "active",
  guide_number: "", name_ru: "", name_en: "", bio_ru: "", languages_ru: "",
  regions_ru: "", portrait_url: "", telegram_url: "", whatsapp_url: "", instagram_url: "", is_public: true,
};

const GuidesTab = () => {
  const [guides, setGuides] = useState([]);
  const [form, setForm] = useState(emptyGuide);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try { setGuides((await api.get("/admin/guides")).data); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const select = (g) => setForm({
    ...emptyGuide, ...g, password: "", is_public: g.is_public !== 0 && g.is_public !== false,
  });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      delete payload.id;
      if (!payload.password) delete payload.password;
      if (form.id) {
        await api.patch(`/admin/guides/${form.id}`, payload);
        toast.success("Гид сохранён");
      } else {
        if (!form.password) throw Object.assign(new Error("Укажите временный пароль"), { response: { data: { detail: "Для нового гида задайте пароль минимум из 12 символов" } } });
        await api.post("/admin/guides", payload);
        toast.success("Аккаунт гида создан");
      }
      setForm(emptyGuide);
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail || err.message));
    } finally { setSaving(false); }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[300px_1fr] gap-8">
      <div>
        <button onClick={() => setForm(emptyGuide)} className="btn-bronze w-full mb-4 flex items-center justify-center gap-2"><Plus size={15} /> Добавить гида</button>
        <div className="space-y-1 max-h-[65vh] overflow-y-auto no-scrollbar" data-lenis-prevent>
          {guides.map((g) => (
            <button key={g.id} onClick={() => select(g)} className="w-full text-left px-4 py-3 border border-argali-brown/30 hover:border-argali-bronze transition-colors">
              <span className="text-argali-ivory text-sm">{g.name}</span>
              <span className="block text-xs text-argali-sandstone/50 mt-1">{g.email}</span>
              <span className="block text-[0.65rem] uppercase tracking-wider mt-1" style={{ color: g.status === "active" ? "#D7B875" : "#8a7a5e" }}>{g.status} · заявок {g.assigned_requests || 0}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="border border-argali-brown/30 p-6 md:p-8">
        <div className="flex items-center justify-between gap-4 mb-6">
          <h3 className="font-serif text-2xl text-argali-ivory">{form.id ? "Редактирование гида" : "Новый гид"}</h3>
          {form.id && <span className="text-xs text-argali-sandstone/50">Экскурсий: {form.excursions_count || 0}</span>}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div><label className={labelCls}>Имя для входа/кабинета</label><input className={fieldCls} value={form.name} onChange={(e) => set("name", e.target.value)} /></div>
          <div><label className={labelCls}>Email для входа</label><input type="email" className={fieldCls} value={form.email} onChange={(e) => set("email", e.target.value)} /></div>
          <div><label className={labelCls}>Телефон</label><input className={fieldCls} value={form.phone || ""} onChange={(e) => set("phone", e.target.value)} /></div>
          <div><label className={labelCls}>{form.id ? "Новый пароль (оставьте пустым без смены)" : "Временный пароль — минимум 12 символов"}</label><input type="password" className={fieldCls} value={form.password || ""} onChange={(e) => set("password", e.target.value)} /></div>
          <div><label className={labelCls}>Статус аккаунта</label><select className={fieldCls} value={form.status} onChange={(e) => set("status", e.target.value)}><option value="active">active</option><option value="pending">pending</option><option value="disabled">disabled</option></select></div>
          <div><label className={labelCls}>Номер гида в реестре</label><input className={fieldCls} value={form.guide_number || ""} onChange={(e) => set("guide_number", e.target.value)} /></div>
          <div><label className={labelCls}>Имя (RU)</label><input className={fieldCls} value={form.name_ru || ""} onChange={(e) => set("name_ru", e.target.value)} /></div>
          <div><label className={labelCls}>Name (EN)</label><input className={fieldCls} value={form.name_en || ""} onChange={(e) => set("name_en", e.target.value)} /></div>
          <div className="md:col-span-2"><label className={labelCls}>О гиде</label><textarea rows={3} className={fieldCls} value={form.bio_ru || ""} onChange={(e) => set("bio_ru", e.target.value)} /></div>
          <div><label className={labelCls}>Языки</label><input className={fieldCls} value={form.languages_ru || ""} onChange={(e) => set("languages_ru", e.target.value)} /></div>
          <div><label className={labelCls}>Регионы/направления</label><input className={fieldCls} value={form.regions_ru || ""} onChange={(e) => set("regions_ru", e.target.value)} /></div>
          <div className="md:col-span-2"><label className={labelCls}>URL портрета</label><input className={fieldCls} value={form.portrait_url || ""} onChange={(e) => set("portrait_url", e.target.value)} /></div>
          <div><label className={labelCls}>Telegram</label><input className={fieldCls} value={form.telegram_url || ""} onChange={(e) => set("telegram_url", e.target.value)} /></div>
          <div><label className={labelCls}>WhatsApp</label><input className={fieldCls} value={form.whatsapp_url || ""} onChange={(e) => set("whatsapp_url", e.target.value)} /></div>
          <div className="md:col-span-2"><label className="flex items-center gap-3 text-sm text-argali-ivory/80"><input type="checkbox" checked={!!form.is_public} onChange={(e) => set("is_public", e.target.checked)} className="accent-argali-bronze" /> Показывать гида публично</label></div>
        </div>
        <button onClick={save} disabled={saving} className="btn-bronze mt-7 flex items-center gap-2"><Save size={15} /> {saving ? "…" : (form.id ? "Сохранить гида" : "Создать аккаунт гида")}</button>
      </div>
    </div>
  );
};

/* ------------------------------ EXCURSIONS ------------------------------ */
const blankExcursion = {
  title_ru: "", title_en: "", short_description_ru: "", short_description_en: "",
  price_from_rub: "", duration: "", difficulty: "easy", category: "", children_note: "",
  featured: false, status: "draft", guide_user_id: "",
};

const ExcursionsTab = ({ user }) => {
  const manager = isManagerUser(user);
  const [list, setList] = useState([]);
  const [guides, setGuides] = useState([]);
  const [sel, setSel] = useState(null);
  const [form, setForm] = useState(blankExcursion);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      setList((await api.get("/admin/excursions")).data);
      if (manager) setGuides((await api.get("/admin/guides")).data);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const select = (e) => {
    setSel(e);
    setForm({
      ...blankExcursion,
      title_ru: e.title_ru || "", title_en: e.title_en || "",
      short_description_ru: e.short_description_ru || "", short_description_en: e.short_description_en || "",
      price_from_rub: e.price_from_rub == null ? "" : e.price_from_rub,
      duration: e.duration || "", difficulty: e.difficulty || "easy", category: e.category || "",
      children_note: e.children_note || "", featured: !!e.featured, status: e.status || "draft",
      guide_user_id: e.guide_user_id || "",
    });
  };
  const createNew = () => { setSel(null); setForm({ ...blankExcursion, status: manager ? "draft" : "pending" }); };
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form, guide_user_id: form.guide_user_id || null };
      if (sel?.id) {
        await api.patch(`/admin/excursions/${sel.id}`, payload);
        toast.success(manager ? "Экскурсия сохранена" : "Изменения отправлены на согласование");
      } else {
        await api.post("/admin/excursions", payload);
        toast.success(manager ? "Экскурсия создана" : "Предложение экскурсии отправлено администратору");
      }
      setSel(null);
      setForm(blankExcursion);
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const remove = async () => {
    if (!sel?.id) return;
    if (!window.confirm(`Удалить «${sel.title_ru}»?`)) return;
    try {
      await api.delete(`/admin/excursions/${sel.id}`);
      toast.success("Экскурсия удалена");
      setSel(null); setForm(blankExcursion); await load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[300px_1fr] gap-8">
      <div>
        <button onClick={createNew} className="btn-bronze w-full mb-4 flex items-center justify-center gap-2"><Plus size={15} /> {manager ? "Новая экскурсия" : "Предложить экскурсию"}</button>
        <div className="space-y-1 max-h-[70vh] overflow-y-auto no-scrollbar" data-testid="admin-exc-list" data-lenis-prevent>
          {list.map((e) => (
            <button key={e.id} onClick={() => select(e)} data-testid={`admin-exc-item-${e.slug}`}
              className="w-full text-left px-4 py-3 border transition-colors"
              style={{ borderColor: sel?.id === e.id ? "#A96818" : "rgba(52,33,22,0.5)", background: sel?.id === e.id ? "rgba(169,104,24,0.12)" : "transparent" }}>
              <span className="text-argali-ivory text-sm">{e.title_ru}</span>
              <span className="block text-argali-sandstone/50 text-xs mt-0.5">{e.price_from_rub == null ? "Цена по запросу" : `от ${e.price_from_rub} ₽`} · {e.status}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="border border-argali-brown/30 p-6 md:p-8" data-testid="admin-exc-editor">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
          <h3 className="font-serif text-2xl text-argali-ivory">{sel ? sel.title_ru : (manager ? "Новая экскурсия" : "Предложить экскурсию")}</h3>
          {sel && <span className="text-xs uppercase tracking-wider text-argali-gold">{sel.status}</span>}
        </div>
        {!manager && <p className="text-xs text-argali-sandstone/50 mb-5">Новая экскурсия и любые изменения попадут администратору на согласование перед публикацией.</p>}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div><label className={labelCls}>Название (RU)</label><input className={fieldCls} data-testid="exc-title-ru" value={form.title_ru} onChange={(e) => set("title_ru", e.target.value)} /></div>
          <div><label className={labelCls}>Title (EN)</label><input className={fieldCls} data-testid="exc-title-en" value={form.title_en} onChange={(e) => set("title_en", e.target.value)} /></div>
          <div className="md:col-span-2"><label className={labelCls}>Описание (RU)</label><textarea rows={3} className={fieldCls} value={form.short_description_ru} onChange={(e) => set("short_description_ru", e.target.value)} /></div>
          <div className="md:col-span-2"><label className={labelCls}>Description (EN)</label><textarea rows={3} className={fieldCls} value={form.short_description_en} onChange={(e) => set("short_description_en", e.target.value)} /></div>
          <div><label className={labelCls}>Цена от (₽) — пусто = по запросу</label><input type="number" className={fieldCls} value={form.price_from_rub} onChange={(e) => set("price_from_rub", e.target.value)} /></div>
          <div><label className={labelCls}>Продолжительность</label><input className={fieldCls} value={form.duration} onChange={(e) => set("duration", e.target.value)} /></div>
          <div><label className={labelCls}>Сложность</label><select className={fieldCls} value={form.difficulty} onChange={(e) => set("difficulty", e.target.value)}>{difficulties.map((d) => <option key={d} value={d}>{d}</option>)}</select></div>
          <div><label className={labelCls}>Категория</label><input className={fieldCls} value={form.category} onChange={(e) => set("category", e.target.value)} /></div>
          <div className="md:col-span-2"><label className={labelCls}>Примечание о детях</label><input className={fieldCls} value={form.children_note} onChange={(e) => set("children_note", e.target.value)} /></div>
          {manager && <>
            <div><label className={labelCls}>Статус / публикация</label><select className={fieldCls} value={form.status} onChange={(e) => set("status", e.target.value)}>{excursionStatuses.map((s) => <option key={s} value={s}>{s}</option>)}</select></div>
            <div><label className={labelCls}>Ответственный гид</label><select className={fieldCls} value={form.guide_user_id} onChange={(e) => set("guide_user_id", e.target.value)}><option value="">Не назначен</option>{guides.filter((g) => g.status === "active").map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}</select></div>
            <div className="md:col-span-2"><label className="flex items-center gap-3 text-sm text-argali-ivory/80"><input type="checkbox" checked={form.featured} onChange={(e) => set("featured", e.target.checked)} className="accent-argali-bronze" /> Избранный маршрут</label></div>
          </>}
        </div>
        <div className="flex flex-wrap gap-3 mt-8">
          <button onClick={save} disabled={saving} className="btn-bronze flex items-center gap-2"><Save size={15} /> {saving ? "…" : (sel ? "Сохранить" : (manager ? "Создать" : "Отправить предложение"))}</button>
          {sel && <button onClick={remove} className={secondaryBtn + " flex items-center gap-2"}><Trash2 size={14} /> Удалить</button>}
        </div>
      </div>
    </div>
  );
};

/* ------------------------------- SETTINGS ------------------------------- */
const SettingsTab = () => {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/admin/settings").then((r) => setS(r.data)).catch(() => {}); }, []);
  const setC = (k, v) => setS((p) => ({ ...p, contacts: { ...p.contacts, [k]: v } }));
  const setG = (k, v) => setS((p) => ({ ...p, guide: { ...p.guide, [k]: v } }));
  const setA = (v) => setS((p) => ({ ...p, analytics: { ...(p.analytics || {}), yandex_metrica_id: v } }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/settings", { contacts: s.contacts, guide: s.guide, analytics: s.analytics });
      toast.success("Настройки сохранены");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  if (!s) return <p className="text-argali-sandstone/50">Загрузка…</p>;
  const c = s.contacts || {}; const g = s.guide || {}; const a = s.analytics || {};

  return (
    <div className="max-w-3xl space-y-10" data-testid="admin-settings">
      <section>
        <h3 className="eyebrow mb-4">Контакты</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div><label className={labelCls}>Телефон</label><input className={fieldCls} value={c.phone || ""} onChange={(e) => setC("phone", e.target.value)} /></div>
          <div><label className={labelCls}>MAX (номер)</label><input className={fieldCls} value={c.max || ""} onChange={(e) => setC("max", e.target.value)} /></div>
          <div><label className={labelCls}>Email</label><input className={fieldCls} value={c.email || ""} onChange={(e) => setC("email", e.target.value)} /></div>
          <div><label className={labelCls}>Telegram — написать</label><input className={fieldCls} value={c.telegram_dm || ""} onChange={(e) => setC("telegram_dm", e.target.value)} /></div>
          <div><label className={labelCls}>Telegram-канал</label><input className={fieldCls} value={c.telegram_channel || ""} onChange={(e) => setC("telegram_channel", e.target.value)} /></div>
          <div><label className={labelCls}>Instagram</label><input className={fieldCls} value={c.instagram || ""} onChange={(e) => setC("instagram", e.target.value)} /></div>
        </div>
      </section>

      <section>
        <h3 className="eyebrow mb-4">Основной гид на публичной странице</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div><label className={labelCls}>Имя (RU)</label><input className={fieldCls} value={g.name_ru || ""} onChange={(e) => setG("name_ru", e.target.value)} /></div>
          <div><label className={labelCls}>Name (EN)</label><input className={fieldCls} value={g.name_en || ""} onChange={(e) => setG("name_en", e.target.value)} /></div>
          <div><label className={labelCls}>Номер в реестре</label><input className={fieldCls} value={g.guide_number || ""} onChange={(e) => setG("guide_number", e.target.value)} /></div>
          <div><label className={labelCls}>URL портрета</label><input className={fieldCls} value={g.portrait_url || ""} onChange={(e) => setG("portrait_url", e.target.value)} /></div>
          <div className="md:col-span-2"><label className={labelCls}>История (RU)</label><textarea rows={3} className={fieldCls} value={g.story_ru || ""} onChange={(e) => setG("story_ru", e.target.value)} /></div>
          <div className="md:col-span-2"><label className={labelCls}>Story (EN)</label><textarea rows={3} className={fieldCls} value={g.story_en || ""} onChange={(e) => setG("story_en", e.target.value)} /></div>
        </div>
      </section>

      <section>
        <h3 className="eyebrow mb-4">Аналитика</h3>
        <div><label className={labelCls}>Yandex Metrica — номер счётчика</label><input className={fieldCls + " max-w-xs"} value={a.yandex_metrica_id || ""} onChange={(e) => setA(e.target.value)} /></div>
      </section>
      <button onClick={save} disabled={saving} className="btn-bronze">{saving ? "…" : "Сохранить настройки"}</button>
    </div>
  );
};

/* -------------------------------- PAGE -------------------------------- */
export default function Admin() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("bookings");

  useEffect(() => { if (user === false) navigate("/login"); }, [user, navigate]);
  const manager = isManagerUser(user);
  const tabs = useMemo(() => {
    const base = [
      { key: "bookings", label: "Заявки" },
      { key: "excursions", label: "Экскурсии" },
    ];
    if (manager) base.push({ key: "guides", label: "Гиды" }, { key: "settings", label: "Настройки" });
    return base;
  }, [manager]);

  if (user === null) return <div className="min-h-screen" style={{ background: "#11110F" }} />;
  if (!user || !user.role) return null;

  return (
    <div data-testid="admin-page" className="min-h-screen" style={{ background: "#11110F" }}>
      <header className="flex items-center justify-between px-6 md:px-10 h-[72px] border-b border-argali-brown/30">
        <div className="flex items-center gap-2.5">
          <img src="/brand/argali_emblem_ru.png" alt="Логотип АРГАЛИ" width="543" height="501" className="h-8 w-auto" />
          <span className="font-serif text-2xl tracking-[0.2em] text-argali-ivory">АРГАЛИ</span>
          <span className="eyebrow ml-3">{manager ? "Admin" : "Guide"}</span>
        </div>
        <div className="flex items-center gap-5">
          <span className="hidden md:inline text-xs text-argali-sandstone/50">{user.name} · {user.role}</span>
          <button onClick={() => { logout(); navigate("/login"); }} className="flex items-center gap-2 text-argali-sandstone/80 hover:text-argali-gold text-sm" data-testid="admin-logout"><LogOut size={16} /> Выйти</button>
        </div>
      </header>

      <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-10">
        <div className="flex gap-2 mb-10 border-b border-argali-brown/30 overflow-x-auto no-scrollbar">
          {tabs.map((tb) => (
            <button key={tb.key} onClick={() => setTab(tb.key)} className="px-5 py-3 text-sm uppercase tracking-[0.14em] transition-colors -mb-px border-b-2 whitespace-nowrap"
              style={{ borderColor: tab === tb.key ? "#A96818" : "transparent", color: tab === tb.key ? "#F4F0E7" : "#8a7a5e" }}>
              {tb.label}
            </button>
          ))}
        </div>
        {tab === "bookings" && <BookingsTab user={user} />}
        {tab === "excursions" && <ExcursionsTab user={user} />}
        {tab === "guides" && manager && <GuidesTab />}
        {tab === "settings" && manager && <SettingsTab />}
      </div>
    </div>
  );
}